export interface DsvFormatOptions {
  separator: string;
  mimeType: string;
  fileExtension: string;
}

export const DSV_FORMAT_TSV: DsvFormatOptions = {
  separator: '\t',
  mimeType: 'text/tab-separated-values; charset=utf-8',
  fileExtension: 'tsv',
};

export const DSV_FORMAT_CSV: DsvFormatOptions = {
  separator: ',',
  mimeType: 'text/csv; charset=utf-8',
  fileExtension: 'csv',
};

export class DsvUtils {
  public static serialize<T>(data: T[], columnKeys: (keyof T)[], separator: string): string {
    if (!data || data.length === 0 || !columnKeys || columnKeys.length === 0) {
      return '';
    }

    const header = columnKeys.map(String).join(separator);
    const rows = data.map((item) =>
      columnKeys
        .map((key) => {
          const value = item[key];
          if (value === null || value === undefined) {
            return '';
          }
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          return String(value);
        })
        .join(separator),
    );

    return [header, ...rows].join('\n');
  }

  public static parse<T = Record<string, string>>(
    input: string,
    separator: string,
    options?: { columnKeys?: string[]; hasHeaders?: boolean },
  ): { rows: T[]; warnings: string[] } {
    if (!input || !input.trim()) {
      return { rows: [], warnings: ['Empty input'] };
    }

    const lines = DsvUtils.normalizeLines(input);
    if (lines.length === 0) {
      return { rows: [], warnings: ['No data lines found'] };
    }

    const { columnKeys, dataStartIndex } = DsvUtils.resolveColumnKeys(lines, separator, options);
    const warnings: string[] = [];
    const rows: T[] = [];

    for (let i = dataStartIndex; i < lines.length; i++) {
      const values = lines[i].split(separator);
      const row: Record<string, unknown> = {};

      for (let j = 0; j < columnKeys.length; j++) {
        row[columnKeys[j]] = DsvUtils.parseValue(j < values.length ? values[j].trim() : '');
      }

      if (values.length > columnKeys.length) {
        warnings.push(`Row ${i - dataStartIndex + 1} has ${values.length} columns, expected ${columnKeys.length}`);
      }

      rows.push(row as T);
    }

    return { rows, warnings };
  }

  public static download(content: string, fileName: string, format: DsvFormatOptions): void {
    const blob = new Blob(['\ufeff' + content], { type: format.mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.${format.fileExtension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  public static isDelimited(text: string, separator: string): boolean {
    if (!text || !text.includes(separator) || !text.includes('\n')) {
      return false;
    }
    const lines = text.split('\n').filter((line) => line.trim().length > 0);
    return lines.length >= 2 && lines[0].includes(separator) && lines.slice(1).some((line) => line.includes(separator));
  }

  private static normalizeLines(input: string): string[] {
    return input
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .filter((line) => line.trim() !== '');
  }

  private static resolveColumnKeys(
    lines: string[],
    separator: string,
    options?: { columnKeys?: string[]; hasHeaders?: boolean },
  ): { columnKeys: string[]; dataStartIndex: number } {
    const hasHeaders = options?.hasHeaders !== false;

    if (options?.columnKeys) {
      return { columnKeys: options.columnKeys, dataStartIndex: hasHeaders ? 1 : 0 };
    }
    if (hasHeaders) {
      return { columnKeys: lines[0].split(separator).map((h) => h.trim()), dataStartIndex: 1 };
    }
    return { columnKeys: lines[0].split(separator).map((_, i) => `col${i}`), dataStartIndex: 0 };
  }

  private static parseValue(value: string): unknown {
    if (value === '') {
      return '';
    }

    if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }

    return value;
  }
}
